import { fromHTML } from '@lumapps/wrex-core/serialization/html/fromHTML/fromHTML';
import { Editor, Location, Transforms } from '@lumapps/wrex-core/slate';
import { formatFragment } from '@lumapps/wrex-core/slate/formatFragment';
import { createPlugin } from '@lumapps/wrex-core/slate/plugin';
import { WrexEditor } from '@lumapps/wrex-core/types';

import { HTMLPasteEditor, HTMLPasteOptions } from '../types';

const getBeforeOrEndEditorPoint = (editor: Editor, at?: Location | null) => {
    return at ? Editor.before(editor, at) : Editor.end(editor, []);
};

/**
 * Interprets pasted text/html content as HTML.
 */
export const withHTMLPaste = (options: HTMLPasteOptions) => {
    return createPlugin<HTMLPasteEditor, WrexEditor<HTMLPasteEditor>>((editor) => {
        const { insertData } = editor;

        return {
            insertData(data: DataTransfer) {
                const fragment = data.getData('application/x-slate-fragment');
                if (fragment) {
                    insertData(data);
                    return;
                }
                const html = data.getData('text/html');
                if (!html) {
                    insertData(data);
                    return;
                }

                const insertLocation = editor.selection || undefined;
                const after = insertLocation && Editor.after(editor, insertLocation);
                const afterRef = after && Editor.pointRef(editor, after);

                const nodesBefore = fromHTML(html, options);
                // Parse HTML into slate nodes (slate fragment).
                const { textChunks, nodes } = formatFragment(editor, nodesBefore, options.defaultBlockType);

                // Insert slate fragment at editor selection or at the end of the document.
                if (textChunks.length) {
                    Transforms.insertFragment(editor, textChunks, { at: insertLocation });
                }

                if (nodes.length) {
                    Transforms.insertNodes(editor, nodes, { at: insertLocation });
                }
                Transforms.select(editor, getBeforeOrEndEditorPoint(editor, afterRef?.unref()) || []);
            },
        };
    });
};
